import { useCallback, useMemo } from "react";

import Select from "react-select";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { useForm, FormProvider, Controller } from "react-hook-form";

import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import {
  QUERY_DIGITAL_RECEPTION_ACTIVE_MANUAL_REGISTRATION,
  QUERY_DIGITAL_RECEPTION_SETTINGS,
} from "../../../../../config/graphql/query";
import { UPDATE_DIGITAL_RECEPTION_SETTINGS } from "../../../../../config/graphql/mutation";

import { reactSelectCustomStyles } from "../../../../Employees/Employee/Information";

const documentScannerFields = [
  "fullName",
  "documentNumber",
  "nationality",
  "gender",
  "expirationDate",
  "personalNumber",
] as const;

interface FormValues extends IDigitalReceptionSettings {}

const DocumentScanner = () => {
  const methods = useForm<FormValues>({
    shouldUnregister: false,
  });

  const { screenId } = useParams<{ screenId: string }>();

  const { t } = useTranslation(["screens", "common"]);

  const documentScannerEnabled = methods.watch("documentScanner.enabled");

  useQuery<{
    digitalReception: IDigitalReception;
  }>(QUERY_DIGITAL_RECEPTION_SETTINGS, {
    skip: !screenId,
    variables: {
      id: screenId,
    },
    fetchPolicy: "network-only",
    onCompleted: ({ digitalReception: { settings } }) => {
      if (!settings) {
        return;
      }

      methods.reset(settings);
    },
  });

  const [onUpdate] = useMutation(UPDATE_DIGITAL_RECEPTION_SETTINGS, {
    refetchQueries: [
      {
        query: QUERY_DIGITAL_RECEPTION_SETTINGS,
        variables: { id: screenId },
      },
    ],
  });

  const { data: activeManualRegistrationData } = useQuery<{
    activeManualRegistration: IManualRegistration;
  }>(QUERY_DIGITAL_RECEPTION_ACTIVE_MANUAL_REGISTRATION, {
    variables: {
      digitalReceptionId: screenId,
    },
    fetchPolicy: "network-only",
    skip: !screenId,
  });

  const activeManualRegistrationFields = useMemo(
    () =>
      activeManualRegistrationData?.activeManualRegistration?.fields.filter(
        (field) => field.type === "Text" || field.type === "Number",
      ) ?? [],
    [activeManualRegistrationData],
  );

  const handleCopyScannerId = useCallback(() => {
    navigator.clipboard.writeText(screenId).then(() => {
      toast.success<string>(
        t("screens:screen.settings.documentScanner.toast.scannerIdCopied"),
      );
    });
  }, [screenId, t]);

  const onSubmit = useCallback(
    async (settings: FormValues) => {
      const { documentScanner } = settings;

      onUpdate({
        variables: {
          input: {
            id: screenId,
            settings: {
              documentScanner: {
                enabled: documentScanner?.enabled || false,
                ...documentScannerFields.reduce((acc, key) => {
                  const fieldValue = documentScanner?.[key]?.field;
                  acc[key] = {
                    enabled: documentScanner?.[key]?.enabled || false,
                    allowManualInput:
                      documentScanner?.[key]?.allowManualInput ?? true,
                    // eslint-disable-next-line no-nested-ternary
                    field: fieldValue
                      ? typeof fieldValue === "string"
                        ? fieldValue
                        : fieldValue.id
                      : null,
                  };
                  return acc;
                }, {} as Record<string, { enabled: boolean; allowManualInput: boolean; field: string | null }>),
              },
            },
          },
        },
      }).then(() => {
        toast.success<string>(t("screens:screen.screenRoute.toast.updated"));
      });
    },
    [onUpdate, t, screenId],
  );

  return (
    <div className="container-fluid">
      <FormProvider {...methods}>
        <form className="row" onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="col-lg-4">
            <div className="form-group mb-4">
              <h6>{t("screens:screen.settings.documentScanner.title")}</h6>
              <Controller
                name="documentScanner.enabled"
                render={({
                  field: { value = false, name, onChange, ...rest },
                }) => (
                  <div className="form-check my-2">
                    <input
                      id={name}
                      {...rest}
                      type="checkbox"
                      className="form-check-input"
                      checked={value === true}
                      onChange={() => onChange(!value)}
                    />
                    <label className="form-check-label" htmlFor={name}>
                      {t("screens:screen.settings.documentScanner.enabled")}
                    </label>
                  </div>
                )}
              />
              {documentScannerEnabled ? (
                <>
                  <div className="form-group mb-2">
                    <label htmlFor="documentScannerSocketUrl">
                      {t("screens:screen.settings.documentScanner.id")}
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        value={screenId}
                        readOnly
                      />
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={handleCopyScannerId}
                        >
                          {t("common:copy")}
                        </button>
                      </div>
                    </div>
                  </div>
                  <p className="mt-4">
                    {t("screens:screen.settings.documentScanner.fields.title")}
                  </p>
                  {documentScannerFields.map((key) => {
                    const enabled = methods.watch(
                      `documentScanner.${key}.enabled`,
                    );

                    return (
                      <div key={key} className="d-flex flex-column mb-2">
                        <Controller
                          name={`documentScanner.${key}.enabled`}
                          render={({
                            field: { value = false, name, onChange, ...rest },
                          }) => (
                            <div className="form-check my-2">
                              <input
                                id={name}
                                {...rest}
                                type="checkbox"
                                className="form-check-input"
                                checked={value === true}
                                onChange={() => onChange(!value)}
                                disabled={key === "expirationDate"}
                              />
                              <label
                                className="form-check-label"
                                htmlFor={name}
                              >
                                {t(
                                  `screens:screen.settings.documentScanner.fields.${key}.enabled`,
                                )}
                              </label>
                            </div>
                          )}
                        />

                        {enabled && (
                          <div className="flex-grow-1 ml-3">
                            <Controller
                              name={`documentScanner.${key}.field`}
                              rules={{
                                validate: (value) => {
                                  const isEnabled = methods.watch(
                                    `documentScanner.${key}.enabled`,
                                  );

                                  if (isEnabled && !value) {
                                    return t(
                                      "screens:screen.settings.documentScanner.fields.validation.fieldRequired",
                                    );
                                  }
                                  return true;
                                },
                              }}
                              render={({
                                field: { onChange, value, ...props },
                                fieldState: { error },
                              }) => {
                                const selectedValue =
                                  typeof value === "string"
                                    ? activeManualRegistrationFields.find(
                                        (f) => f.id === value,
                                      )
                                    : value;

                                const selectedFieldIds = documentScannerFields
                                  .filter((fieldKey) => fieldKey !== key)
                                  .map((fieldKey) => {
                                    const fieldValue = methods.watch(
                                      `documentScanner.${fieldKey}.field`,
                                    );
                                    return typeof fieldValue === "string"
                                      ? fieldValue
                                      : fieldValue?.id;
                                  })
                                  .filter(Boolean);

                                const availableOptions =
                                  activeManualRegistrationFields.filter(
                                    (field) =>
                                      !selectedFieldIds.includes(field.id),
                                  );

                                return (
                                  <>
                                    <Select
                                      value={selectedValue}
                                      getOptionLabel={({ label }) => label}
                                      getOptionValue={({ id }) => id}
                                      options={availableOptions}
                                      isClearable
                                      onChange={onChange}
                                      styles={reactSelectCustomStyles(!!error)}
                                      {...props}
                                    />

                                    {!!error && (
                                      <div className="invalid-feedback d-block">
                                        {error.message}
                                      </div>
                                    )}
                                  </>
                                );
                              }}
                            />
                            <Controller
                              name={`documentScanner.${key}.allowManualInput`}
                              defaultValue
                              render={({
                                field: {
                                  value = true,
                                  name,
                                  onChange,
                                  ...rest
                                },
                              }) => (
                                <div className="form-check my-1">
                                  <input
                                    id={name}
                                    {...rest}
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={value === true}
                                    onChange={() => onChange(!value)}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor={name}
                                  >
                                    {t(
                                      "screens:screen.settings.documentScanner.fields.allowManualInput",
                                    )}
                                  </label>
                                </div>
                              )}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              ) : null}
            </div>
            <input
              type="submit"
              className="btn btn-primary"
              disabled={methods.formState.isSubmitting}
            />
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default DocumentScanner;
